import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/services/supabase'
import type { Vehicle, FuelType } from '@/types'

export const useVehicleStore = defineStore('vehicle', () => {
  const vehicles = ref<Vehicle[]>([])
  const currentVehicle = ref<Vehicle | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const defaultVehicle = computed(() => 
    vehicles.value.find(v => v.isDefault) || vehicles.value[0] || null
  )

  async function loadVehicles(userId: string) {
    isLoading.value = true
    error.value = null

    try {
      const { data, error: fetchError } = await supabase
        .from('vehicles')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      vehicles.value = (data || []).map(mapVehicleFromDB)
      
      // Set current vehicle to default or first
      if (!currentVehicle.value && vehicles.value.length > 0) {
        currentVehicle.value = defaultVehicle.value
      }

      console.log('✅ Vehicles loaded:', vehicles.value.length)
    } catch (err) {
      console.error('❌ Error loading vehicles:', err)
      error.value = err instanceof Error ? err.message : 'Failed to load vehicles'
    } finally {
      isLoading.value = false
    }
  }

  async function createVehicle(vehicleData: Omit<Vehicle, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) {
    isLoading.value = true
    error.value = null

    try {
      const user = (await supabase.auth.getUser()).data.user
      if (!user) throw new Error('No user logged in')

      const newVehicle = {
        user_id: user.id,
        name: vehicleData.name,
        make: vehicleData.make,
        model: vehicleData.model,
        year: vehicleData.year,
        fuel_type: vehicleData.fuelType,
        fuel_consumption_city: vehicleData.fuelConsumptionCity,
        fuel_consumption_highway: vehicleData.fuelConsumptionHighway,
        fuel_consumption_combined: vehicleData.fuelConsumptionCombined,
        tank_capacity: vehicleData.tankCapacity,
        battery_capacity: vehicleData.batteryCapacity,
        charging_speed: vehicleData.chargingSpeed,
        range_electric: vehicleData.rangeElectric,
        color: vehicleData.color,
        icon: vehicleData.icon,
        is_default: vehicleData.isDefault,
        notes: vehicleData.notes
      }

      const { data, error: insertError } = await supabase
        .from('vehicles')
        .insert(newVehicle)
        .select()
        .single()

      if (insertError) throw insertError

      const vehicle = mapVehicleFromDB(data)
      vehicles.value.unshift(vehicle)

      if (vehicle.isDefault) {
        currentVehicle.value = vehicle
      }

      console.log('✅ Vehicle created:', vehicle.name)
      return vehicle
    } catch (err) {
      console.error('❌ Error creating vehicle:', err)
      error.value = err instanceof Error ? err.message : 'Failed to create vehicle'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateVehicle(vehicleId: string, updates: Partial<Vehicle>) {
    isLoading.value = true
    error.value = null

    try {
      const dbUpdates: any = {}
      
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.make !== undefined) dbUpdates.make = updates.make
      if (updates.model !== undefined) dbUpdates.model = updates.model
      if (updates.year !== undefined) dbUpdates.year = updates.year
      if (updates.fuelType !== undefined) dbUpdates.fuel_type = updates.fuelType
      if (updates.fuelConsumptionCity !== undefined) dbUpdates.fuel_consumption_city = updates.fuelConsumptionCity
      if (updates.fuelConsumptionHighway !== undefined) dbUpdates.fuel_consumption_highway = updates.fuelConsumptionHighway
      if (updates.fuelConsumptionCombined !== undefined) dbUpdates.fuel_consumption_combined = updates.fuelConsumptionCombined
      if (updates.tankCapacity !== undefined) dbUpdates.tank_capacity = updates.tankCapacity
      if (updates.batteryCapacity !== undefined) dbUpdates.battery_capacity = updates.batteryCapacity
      if (updates.chargingSpeed !== undefined) dbUpdates.charging_speed = updates.chargingSpeed
      if (updates.rangeElectric !== undefined) dbUpdates.range_electric = updates.rangeElectric
      if (updates.color !== undefined) dbUpdates.color = updates.color
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon
      if (updates.isDefault !== undefined) dbUpdates.is_default = updates.isDefault
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes

      dbUpdates.updated_at = new Date().toISOString()

      const { data, error: updateError } = await supabase
        .from('vehicles')
        .update(dbUpdates)
        .eq('id', vehicleId)
        .select()
        .single()

      if (updateError) throw updateError

      const updatedVehicle = mapVehicleFromDB(data)
      const index = vehicles.value.findIndex(v => v.id === vehicleId)
      if (index !== -1) {
        vehicles.value[index] = updatedVehicle
      }

      if (currentVehicle.value?.id === vehicleId) {
        currentVehicle.value = updatedVehicle
      }

      console.log('✅ Vehicle updated:', updatedVehicle.name)
      return updatedVehicle
    } catch (err) {
      console.error('❌ Error updating vehicle:', err)
      error.value = err instanceof Error ? err.message : 'Failed to update vehicle'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteVehicle(vehicleId: string) {
    isLoading.value = true
    error.value = null

    try {
      const { error: deleteError } = await supabase
        .from('vehicles')
        .delete()
        .eq('id', vehicleId)

      if (deleteError) throw deleteError

      vehicles.value = vehicles.value.filter(v => v.id !== vehicleId)

      if (currentVehicle.value?.id === vehicleId) {
        currentVehicle.value = defaultVehicle.value
      }

      console.log('✅ Vehicle deleted')
    } catch (err) {
      console.error('❌ Error deleting vehicle:', err)
      error.value = err instanceof Error ? err.message : 'Failed to delete vehicle'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function setCurrentVehicle(vehicleId: string) {
    const vehicle = vehicles.value.find(v => v.id === vehicleId)
    if (vehicle) {
      currentVehicle.value = vehicle
      console.log('🚗 Current vehicle set:', vehicle.name)
    }
  }

  function mapVehicleFromDB(data: any): Vehicle {
    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      make: data.make,
      model: data.model,
      year: data.year,
      fuelType: data.fuel_type,
      fuelConsumptionCity: data.fuel_consumption_city,
      fuelConsumptionHighway: data.fuel_consumption_highway,
      fuelConsumptionCombined: data.fuel_consumption_combined,
      tankCapacity: data.tank_capacity,
      batteryCapacity: data.battery_capacity,
      chargingSpeed: data.charging_speed,
      rangeElectric: data.range_electric,
      color: data.color,
      icon: data.icon,
      isDefault: data.is_default,
      notes: data.notes,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    }
  }

  function reset() {
    vehicles.value = []
    currentVehicle.value = null
    error.value = null
  }

  async function initialize(userId: string) {
    await loadVehicles(userId)
  }

  return {
    vehicles,
    currentVehicle,
    defaultVehicle,
    isLoading,
    error,
    loadVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    setCurrentVehicle,
    initialize,
    reset
  }
})
